import { useTranslation } from 'react-i18next';
import { currentLocale } from '@shared/utils/dateFormatter';
import { useQuery } from '@tanstack/react-query';
import { useSystemInfo } from '@shared/hooks/useSystemInfo';
import { useState } from 'react';
import { Modal, Spin } from 'antd';
import { 
  QuestionCircleOutlined, 
  ClockCircleOutlined 
} from '@ant-design/icons';
import type { PerformanceInfo, SupportStatus } from '@shared/types/system';

interface AppFooterProps {
  showSupport?: boolean;
  showPerformance?: boolean;
}

export function AppFooter({ 
  showSupport = true, 
  showPerformance = true 
}: AppFooterProps) {
  const { t } = useTranslation(['translation']);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [perfDetailsOpen, setPerfDetailsOpen] = useState(false);

  // Загрузка информации о системе
  const { data: systemInfo, isLoading: systemLoading } = useSystemInfo();

  // Загрузка performance info (только для debug)
  const { data: perfInfo } = useQuery<PerformanceInfo>({
    queryKey: ['performanceInfo'],
    queryFn: () => import('@shared/api/systemApi').then(m => m.getPerformanceInfo()),
    enabled: showPerformance,
    staleTime: 0, // Всегда свежие данные
    refetchInterval: showPerformance ? 5000 : false, // Обновление каждые 5 секунд в debug режиме
  });

  // Определение статуса поддержки
  const getSupportStatus = (): SupportStatus => {
    if (!systemInfo?.supportDate) return 'normal';
    
    const supportDate = new Date(systemInfo.supportDate);
    const now = new Date();
    const daysLeft = Math.floor((supportDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return 'expired';
    if (daysLeft < 30) return 'warning';
    return 'normal';
  };

  const supportStatus = getSupportStatus();

  // Цвета статуса поддержки
  const supportColors = {
    expired: 'bg-red-600 hover:bg-red-700',
    warning: 'bg-yellow-500 hover:bg-yellow-600',
    normal: 'bg-blue-600 hover:bg-blue-700',
  };

  // Форматирование даты
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLocale());
  };

  // Форматирование размера памяти
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (systemLoading) {
    return (
      <footer className="border-t-2 border-gray-200 dark:border-gray-700 py-2 px-3 bg-white dark:bg-gray-800">
        <div className="flex justify-center">
          <Spin size="small" />
        </div>
      </footer>
    );
  }

  const currentYear = new Date().getFullYear();
  const startYear = systemInfo?.startYear || currentYear;
  const yearRange = startYear === currentYear ? currentYear : `${startYear}–${currentYear}`;

  return (
    <>
      <footer className="
        border-t-2 border-gray-200 dark:border-gray-700
        py-2 md:py-3
        mt-2 md:mt-3
        px-3 md:px-4
        bg-white dark:bg-gray-800
        text-gray-600 dark:text-gray-400
        text-xs
        relative
      ">
        <div className="max-w-7xl mx-auto">
          {/* Desktop версия (>= 768px) */}
          <div className="hidden md:flex md:items-center md:justify-between">
            {/* Левая часть: Copyright */}
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 dark:text-gray-500">
                {t('footer.copyright')} {yearRange}
              </span>
              <a 
                href={`https://${systemInfo?.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {systemInfo?.domain}
              </a>
              <span className="text-gray-400 dark:text-gray-600">|</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {systemInfo?.companyName}
                {systemInfo?.revision && (
                  <span className="ml-1 text-gray-500 dark:text-gray-500 italic">
                    "{systemInfo.revision}"
                  </span>
                )}
              </span>
            </div>

            {/* Правая часть: Performance + Support */}
            <div className="flex items-center space-x-4">
              {/* Performance Info */}
              {showPerformance && perfInfo && (
                <button
                  onClick={() => setPerfDetailsOpen(!perfDetailsOpen)}
                  className="
                    px-2 py-1 
                    text-xs 
                    text-white 
                    bg-gray-600 hover:bg-gray-700 
                    rounded
                    transition-colors
                    cursor-pointer
                  "
                  title={t('footer.performanceInfo')}
                >
                  P
                </button>
              )}

              {/* Support кнопка */}
              {showSupport && (
                <button
                  onClick={() => setSupportModalOpen(true)}
                  className={`
                    px-3 py-1 
                    text-xs md:text-sm
                    text-white
                    rounded
                    transition-colors
                    flex items-center gap-2
                    ${supportColors[supportStatus]}
                  `}
                  title={
                    supportStatus === 'expired' 
                      ? `${t('footer.supportExpired')}: ${formatDate(systemInfo?.supportDate)}`
                      : supportStatus === 'warning'
                      ? `${t('footer.supportExpiresOn')}: ${formatDate(systemInfo?.supportDate)}`
                      : t('footer.support')
                  }
                >
                  <QuestionCircleOutlined />
                  <span className="hidden lg:inline">{t('footer.support')}</span>
                  {supportStatus !== 'normal' && (
                    <ClockCircleOutlined className="animate-pulse" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Mobile версия (< 768px) */}
          <div className="md:hidden flex flex-col space-y-2">
            {/* Copyright */}
            <div className="text-center">
              <span className="text-gray-500 dark:text-gray-500">
                {t('footer.copyright')} {currentYear}
              </span>
              {' '}
              <a 
                href={`https://${systemInfo?.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {systemInfo?.domain}
              </a>
            </div>

            {/* Version */}
            <div className="text-center text-xs">
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {systemInfo?.companyName}
              </span>
            </div>

            {/* Support кнопка (mobile) */}
            {showSupport && (
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => setSupportModalOpen(true)}
                  className={`
                    px-4 py-2 
                    text-sm
                    text-white
                    rounded-lg
                    transition-colors
                    flex items-center gap-2
                    ${supportColors[supportStatus]}
                    min-h-[44px]
                  `}
                >
                  <QuestionCircleOutlined />
                  <span>{t('footer.support')}</span>
                  {supportStatus !== 'normal' && (
                    <ClockCircleOutlined className="animate-pulse" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Performance Details (выпадающая панель) */}
          {showPerformance && perfInfo && perfDetailsOpen && (
            <div className="
              mt-4 p-3
              bg-gray-100 dark:bg-gray-900
              border border-gray-300 dark:border-gray-700
              rounded
              text-xs
              hidden md:block
            ">
              <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                {perfInfo.cpuUsage !== undefined && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      CPU:
                    </span>
                    {' '}
                    <span className={`
                      text-gray-600 dark:text-gray-400
                      ${perfInfo.cpuUsage > 80 ? 'text-red-600 font-bold' : perfInfo.cpuUsage > 50 ? 'text-yellow-600' : ''}
                    `}>
                      {perfInfo.cpuUsage.toFixed(1)}%
                    </span>
                  </div>
                )}
                {perfInfo.ramUsage !== undefined && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      RAM:
                    </span>
                    {' '}
                    <span className={`
                      text-gray-600 dark:text-gray-400
                      ${perfInfo.ramUsage > 85 ? 'text-red-600 font-bold' : perfInfo.ramUsage > 70 ? 'text-yellow-600' : ''}
                    `}>
                      {perfInfo.ramUsage.toFixed(1)}%
                    </span>
                  </div>
                )}
                {perfInfo.dbOpenConnections !== undefined && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      DB Conns:
                    </span>
                    {' '}
                    <span className={`
                      text-gray-600 dark:text-gray-400
                      ${perfInfo.dbStatus !== 'ok' ? 'text-red-600 font-bold' : ''}
                    `}>
                      {perfInfo.dbOpenConnections} (InUse: {perfInfo.dbInUseConnections})
                    </span>
                  </div>
                )}
                {perfInfo.redisStatus !== undefined && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Redis:
                    </span>
                    {' '}
                    <span className={`
                      text-gray-600 dark:text-gray-400
                      ${perfInfo.redisStatus !== 'ok' ? 'text-red-600 font-bold' : 'text-green-600'}
                    `}>
                      {perfInfo.redisStatus}
                    </span>
                  </div>
                )}
                {perfInfo.lastUpdate !== undefined && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Freshness:
                    </span>
                    {' '}
                    <span className={`
                      text-gray-600 dark:text-gray-400
                      ${(Date.now() / 1000 - perfInfo.lastUpdate) > 15 ? 'text-red-600' : ''}
                    `}>
                      {Math.round(Date.now() / 1000 - perfInfo.lastUpdate)}s ago
                    </span>
                  </div>
                )}
                {perfInfo.pageGenerateTime > 0 && (
                  <div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300">
                      Page:
                    </span>
                    {' '}
                    <span className="text-gray-600 dark:text-gray-400">
                      {perfInfo.pageGenerateTime.toFixed(3)}s
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </footer>

      {/* Support Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <QuestionCircleOutlined className="text-blue-600" />
            <span className="uppercase font-semibold">
              {t('footer.support')}
            </span>
          </div>
        }
        open={supportModalOpen}
        onCancel={() => setSupportModalOpen(false)}
        footer={null}
        width={500}
        className="support-modal"
      >
        <div className="space-y-4">
          {/* Статус поддержки */}
          {systemInfo?.supportDate && (
            <div className={`
              p-4 rounded-lg
              ${supportStatus === 'expired' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : ''}
              ${supportStatus === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : ''}
              ${supportStatus === 'normal' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : ''}
            `}>
              <div className="flex items-center gap-2">
                <ClockCircleOutlined className={`
                  text-lg
                  ${supportStatus === 'expired' ? 'text-red-600 dark:text-red-400' : ''}
                  ${supportStatus === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                  ${supportStatus === 'normal' ? 'text-green-600 dark:text-green-400' : ''}
                `} />
                <div>
                  <div className="font-semibold">
                    {supportStatus === 'expired' && t('footer.supportExpired')}
                    {supportStatus === 'warning' && t('footer.supportExpiresOn')}
                    {supportStatus === 'normal' && t('footer.supportExpiresOn')}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(systemInfo.supportDate)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Контакты поддержки */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200">
              Контакты поддержки:
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-center gap-2">
                📧 <a href="mailto:support@workernet.example" className="text-blue-600 dark:text-blue-400 hover:underline">
                  support@workernet.ru
                </a>
              </li>
              <li className="flex items-center gap-2">
                📞 <span>+7 (34345) 47-2-47</span>
              </li>
              <li className="flex items-center gap-2">
                💬 <span>Telegram: @WorkerNet_support</span>
              </li>
            </ul>
          </div>

          {/* Полезные ссылки */}
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200">
              Полезные ссылки:
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="http://wiki.workernet.ru/" className="text-blue-600 dark:text-blue-400 hover:underline">
                  📖 Документация
                </a>
              </li>
              <li>
                <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">
                  🐛 Сообщить о проблеме
                </a>
              </li>
              <li>
                <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">
                  💡 База знаний
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Modal>
    </>
  );
}

